import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import type { DateRange, TimelineDay } from './types';
import type { ProjectTeamMemberDto } from 'domain/shared/newProject';
import type { ProjectTeamMemberLite } from 'domain/shared/newProject/dashboard/types';
import { PROJECT_MEMBERSHIP_ROLES_KEY, Role } from '../core/constants';

export type HeatmapTeamMember = Pick<ProjectTeamMemberDto, 'employeeAlias' | 'role'> | ProjectTeamMemberLite;

const HEATMAP_EXCLUDED_ROLES = [
  Role.CEO,
  Role.CTO,
  Role.COO,
  Role.CFO,
  PROJECT_MEMBERSHIP_ROLES_KEY.ACCOUNT_MANAGER,
] as const;

const isVisibleHeatmapRole = (member: HeatmapTeamMember): boolean => {
  if (!member.role) return true;
  const normalized = member.role.trim().toUpperCase();
  return !HEATMAP_EXCLUDED_ROLES.some(role => normalized === role);
};

const dedupByAlias = <T extends HeatmapTeamMember>(members: T[]): T[] => {
  const seen = new Set<string>();
  return members.filter(m => {
    if (!m.employeeAlias || seen.has(m.employeeAlias)) return false;
    seen.add(m.employeeAlias);
    return true;
  });
};

export const getVisibleHeatmapTeamMembers = <T extends HeatmapTeamMember>(members: T[]): T[] =>
  dedupByAlias(members).filter(isVisibleHeatmapRole);

export const DAY_CELL_WIDTH = 19;
const DAY_GAP = 1;
export const DAY_STEP = DAY_CELL_WIDTH + DAY_GAP;

export const getTodayIsoDate = (): string => moment().format(DATE_FORMAT.API_ONLY_DATE);

export const findTodayIndex = (days: TimelineDay[]): number => {
  if (days.length === 0) return -1;
  const today = getTodayIsoDate();
  if (today < days[0].date || today > days[days.length - 1].date) return -1;
  return days.findIndex(d => d.date === today);
};

export const filterToDateRange = (filter: Record<string, unknown>, dateRangeKey = 'dateRange'): DateRange | null => {
  const range = filter[dateRangeKey] as { ge?: string; le?: string } | undefined;
  if (!range?.ge || !range?.le) return null;
  const start = range.ge;
  const end = range.le;
  if (!start || !end) return null;
  return {
    start: start <= end ? start : end,
    end: start <= end ? end : start,
  };
};

const nextDay = (date: string) => moment(date).add(1, 'day').format('YYYY-MM-DD');

export const mergeOverlappingPeriods = (
  periods: { startDate: string; endDate: string }[],
): { startDate: string; endDate: string }[] => {
  if (!periods.length) return [];
  const sorted = [...periods].sort((a, b) => a.startDate.localeCompare(b.startDate));
  return sorted.reduce<{ startDate: string; endDate: string }[]>((merged, curr) => {
    const last = merged.at(-1);
    if (!last || curr.startDate > nextDay(last.endDate)) {
      merged.push({ startDate: curr.startDate, endDate: curr.endDate });
    } else {
      last.endDate = curr.endDate > last.endDate ? curr.endDate : last.endDate;
    }
    return merged;
  }, []);
};

const dateStringsBetween = (range: DateRange): string[] => {
  const { start, end } = range;
  if (!start || !end || start > end) return [];
  const startM = moment.utc(start);
  const daysCount = moment.utc(end).diff(startM, 'days') + 1;
  return Array.from({ length: daysCount }, (_, i) => startM.clone().add(i, 'day').format(DATE_FORMAT.API_ONLY_DATE));
};

export const buildTimelineDays = (range: DateRange): TimelineDay[] => {
  return dateStringsBetween(range).map(date => {
    const m = moment(date);
    return {
      date,
      day: m.date(),
      monthLabel: m.format(DATE_FORMAT.ONLY_MONTH),
    };
  });
};

const getDayIndex = (days: TimelineDay[], dateStr: string): number => {
  if (!dateStr || days.length === 0) return -1;
  const idx = days.findIndex(d => d.date === dateStr);
  return idx >= 0 ? idx : -1;
};

const clampDatesToRange = (
  startDate: string,
  endDate: string,
  rangeStart: string,
  rangeEnd: string,
): { start: string; end: string } | null => {
  if (startDate > rangeEnd || endDate < rangeStart) return null;
  const start = startDate < rangeStart ? rangeStart : startDate;
  const end = endDate > rangeEnd ? rangeEnd : endDate;
  return { start, end };
};

export const getBarPixels = (
  days: TimelineDay[],
  startDate: string,
  endDate: string,
): { left: number; width: number } => {
  if (!days.length) return { left: 0, width: 0 };
  const rangeStart = days[0].date;
  const rangeEnd = days[days.length - 1].date;
  const clamped = clampDatesToRange(startDate, endDate ?? startDate, rangeStart, rangeEnd);
  if (!clamped) return { left: 0, width: 0 };
  const { start: clampedStart, end: clampedEnd } = clamped;
  const startIdx = getDayIndex(days, clampedStart);
  const endIdx = clampedEnd < clampedStart ? startIdx : getDayIndex(days, clampedEnd);
  if (startIdx === -1 || endIdx === -1) return { left: 0, width: 0 };
  const left = startIdx * DAY_STEP;
  const width = (endIdx - startIdx) * DAY_STEP + DAY_CELL_WIDTH;
  return { left, width };
};
